import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface KlingV16Params {
  /**
   * 视频生成提示词，必须提供且长度在1-2500字符之间
   */
  prompt: string;
  /**
   * 负面提示词
   */
  negative_prompt?: string;
  /**
   * 提示词相关性强度
   */
  cfg_scale?: number;
  /**
   * 生成模式，标准(std)或专业(pro)
   * @values std | pro
   */
  mode?: 'std' | 'pro';
  /**
   * 视频宽高比，支持16:9、9:16、1:1
   * @values 16:9 | 9:16 | 1:1
   */
  aspect_ratio?: '16:9' | '9:16' | '1:1';
  /**
   * 控制摄像机运动的参数（如果未指定，模型将根据输入文本/图像智能匹配）
   */
  camera_control?: {
    /**
     * 预定义摄像机运动类型 - simple: 基础摄像机运动，在此类型下可在config中选择6个选项之一 - down_back: 摄像机下降并向后移动（向下平移并缩小） - forward_up: 摄像机向前移动并向上倾斜（放大并向上平移） - right_turn_forward: 向右旋转并向前移动 - left_turn_forward: 向左旋转并向前移动
     * @values simple | down_back | forward_up | right_turn_forward | left_turn_forward
     */
    type?: 'simple' | 'down_back' | 'forward_up' | 'right_turn_forward' | 'left_turn_forward';
    /**
     * 包含6个字段，用于指定摄像机在不同方向上的运动或变化 当type设置为simple时为必填，其他类型时应留空 要求6个参数中只能有一个非零，其余必须为零
     */
    config?: Record<string, any>;
  };
  /**
   * 视频时长（秒），支持5-10秒
   */
  duration?: number;
  /**
   * 外部任务ID
   */
  external_task_id?: string;
}

export interface KlingV16Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_v1_6
 *
 * Kling AI V1.6 Video Generator
 * 
 * Model: kling-v1-6
 * Features: 文本生成视频 (Text-to-Video)
 * Duration: 5-10秒
 * Aspect Ratios: 16:9, 9:16, 1:1
 * Modes: std(标准), pro(专业)
 * 
 * API Documentation: https://app.klingai.com/global/dev/document-api/apiReference/model/textToVideo
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingV16 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingV16({
 *   // 参数见 KlingV16Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingV16Params 接口定义
 * @returns 生成的资源列表
 */
export async function klingV16(
  params: KlingV16Params,
): Promise<KlingV16Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_v1_6',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingV16Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
